import {
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBrandWhatsapp,
  IconCheck,
  IconPlus,
  IconShield,
  IconTrendingUp,
} from "@tabler/icons-react";
import { PendulumBadge } from "$/components/pendulum-badge";
import { Reveal } from "$/components/reveal";
import { Section } from "$/components/section";
import { addOns } from "$/data/add-ons";
import { WHATSAPP_URL } from "$/data/links";

const coreFeatures = [
  "Vendas, catálogo e balanço",
  "Cadastro de clientes e fornecedores",
  "Impressão térmica de cupom",
  "Exportação e importação em XLSX",
  "Backup local",
];

const maintenanceFeatures = [
  "Backup automático na nuvem",
  "Suporte por WhatsApp prioritário",
  "Correção prioritária de bugs",
  "Visitas técnicas sem custo adicional",
];

export function Pricing() {
  return (
    <Section id="pricing">
      <Stack gap="sm" align="center" mb={56} ta="center">
        <Box mb={4} style={{ minHeight: 34 }}>
          <PendulumBadge />
        </Box>
        <Title
          order={2}
          fz={{ base: 32, md: 48 }}
          fw={800}
          lh={1.08}
          ta="center"
          style={{ letterSpacing: "-0.03em" }}
        >
          Preço honesto,{" "}
          <Text
            component="span"
            inherit
            variant="gradient"
            gradient={{ from: "green.7", to: "green.5", deg: 135 }}
          >
            sem letra miúda.
          </Text>
        </Title>
        <Text c="gray.7" fz={{ base: 16, md: 18 }} ta="center" maw={620} lh={1.55}>
          Você compra uma vez e o sistema é seu pra sempre. Sem aluguel obrigatório, só um plano de
          manutenção opcional, se você quiser.
        </Text>
      </Stack>

      <Card
        maw={960}
        mx="auto"
        padding={0}
        radius="xl"
        withBorder
        shadow="xl"
        style={{
          borderColor: "var(--mantine-color-gray-2)",
          overflow: "hidden",
        }}
      >
        <Grid>
          {/* LADO ESQUERDO — branco */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Box
              p={{ base: 28, md: 48 }}
              pos="relative"
              style={{ background: "white", height: "100%" }}
            >
              <Group gap={8} mb="xl" align="center" justify="space-between">
                <Group>
                  <Box
                    w={6}
                    h={6}
                    style={{
                      borderRadius: "50%",
                      background: "var(--mantine-color-green-6)",
                    }}
                  />
                  <Text
                    fz="xs"
                    fw={700}
                    c="green.7"
                    tt="uppercase"
                    style={{ letterSpacing: "0.12em" }}
                  >
                    Verdana Básico
                  </Text>
                  <Box
                    w={6}
                    h={6}
                    style={{
                      borderRadius: "50%",
                      background: "var(--mantine-color-green-6)",
                    }}
                  />
                </Group>

                <Badge
                  variant="light"
                  color="orange"
                  size="sm"
                  radius="lg"
                  style={{ fontWeight: 700, letterSpacing: "0.04em" }}
                >
                  vitalício
                </Badge>
              </Group>

              <Title
                order={3}
                fz={{ base: 28, md: 40 }}
                fw={700}
                lh={1.1}
                mb={{ base: 32, md: 48 }}
                c="dark.9"
                style={{ letterSpacing: "-0.03em" }}
              >
                <Text
                  inherit
                  fs="italic"
                  fw={600}
                  variant="gradient"
                  gradient={{ from: "green.7", to: "green.5", deg: 135 }}
                  style={{
                    display: "inline-block",
                    paddingRight: "0.15em",
                    paddingBottom: "0.05em",
                    marginRight: "-0.05em",
                    lineHeight: 1.15,
                  }}
                >
                  Perfeito
                </Text>{" "}
                pro MEI e pra
                <br />
                quem está começando
              </Title>

              <Group align="baseline" gap={6} mb="lg">
                <Text fz={{ base: 22, md: 28 }} c="dark.9" fw={800}>
                  R$
                </Text>
                <Text
                  component="span"
                  fz={{ base: 64, md: 80 }}
                  fw={800}
                  lh={1}
                  c="dark.9"
                  style={{ letterSpacing: "-0.04em" }}
                >
                  199
                </Text>
                <Text fz={{ base: 16, md: 18 }} c="gray.6" fw={500} ml={4}>
                  /uma vez
                </Text>
              </Group>

              <Group gap="sm" wrap="wrap">
                <Button
                  size="lg"
                  variant="gradient"
                  gradient={{ from: "green.7", to: "green.5", deg: 135 }}
                  leftSection={<IconBrandWhatsapp size={20} />}
                  component="a"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener"
                  radius="xl"
                  style={{
                    boxShadow: "0 12px 32px -8px var(--mantine-color-green-4)",
                  }}
                >
                  Quero o Verdana
                </Button>
              </Group>
            </Box>
          </Grid.Col>

          {/* LADO DIREITO — gradiente verde */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Box
              p={{ base: 28, md: 40 }}
              pos="relative"
              style={{
                background:
                  "linear-gradient(135deg, var(--mantine-color-green-5) 0%, var(--mantine-color-green-7) 60%, var(--mantine-color-green-8) 100%)",
                height: "100%",
                overflow: "hidden",
              }}
            >
              {/* Brilhos decorativos */}
              <Box
                pos="absolute"
                top={-80}
                right={-80}
                w={240}
                h={240}
                style={{
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 70%)",
                  filter: "blur(20px)",
                }}
              />
              <Box
                pos="absolute"
                bottom={-60}
                left={-60}
                w={200}
                h={200}
                style={{
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)",
                  filter: "blur(20px)",
                }}
              />

              <Stack gap="lg" pos="relative" style={{ zIndex: 1 }}>
                <Text fw={700} fz={{ base: 18, md: 20 }} c="white">
                  Incluso
                </Text>

                <Stack gap={14}>
                  {coreFeatures.map((f) => (
                    <Group key={f} gap={12} wrap="nowrap" align="center">
                      <ThemeIcon
                        size={22}
                        radius="xl"
                        variant="filled"
                        style={{
                          background: "transparent",
                          border: "1px solid white",
                        }}
                      >
                        <IconCheck size={13} stroke={3} color="white" />
                      </ThemeIcon>
                      <Text fz={{ base: 14, md: 15 }} c="white" lh={1.4} fw={500}>
                        {f}
                      </Text>
                    </Group>
                  ))}

                  <Group gap={12} wrap="nowrap" align="center">
                    <ThemeIcon
                      size={22}
                      radius="xl"
                      variant="filled"
                      style={{
                        background: "transparent",
                        border: "1px solid white",
                      }}
                    >
                      <IconPlus size={13} stroke={3} color="white" />
                    </ThemeIcon>
                    <Text fz={{ base: 14, md: 15 }} c="white" lh={1.4} fw={500}>
                      E muito mais...
                    </Text>
                  </Group>
                </Stack>

                {/* Sub-card "precisa de ajuda" — branco com leve glass */}
                <Card
                  mt="md"
                  padding="md"
                  radius="lg"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.9) 100%)",
                    backdropFilter: "blur(20px) saturate(160%)",
                    WebkitBackdropFilter: "blur(20px) saturate(160%)",
                    border: "1px solid rgba(255,255,255,0.9)",
                    boxShadow:
                      "0 10px 28px -8px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,1)",
                  }}
                >
                  <Group gap="sm" align="flex-start" wrap="nowrap" mb={12}>
                    <ThemeIcon
                      size={36}
                      radius="md"
                      variant="gradient"
                      gradient={{ from: "green.7", to: "green.5", deg: 135 }}
                      style={{ flexShrink: 0 }}
                    >
                      <IconBrandWhatsapp size={20} />
                    </ThemeIcon>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        fz="xs"
                        fw={700}
                        c="green.8"
                        tt="uppercase"
                        style={{ letterSpacing: "0.06em" }}
                      >
                        Precisa de ajuda?
                      </Text>
                      <Text fz="sm" fw={700} c="dark.9" lh={1.3} mt={2}>
                        Não sabe se serve pro seu negócio?
                      </Text>
                    </Box>
                  </Group>
                  <Button
                    fullWidth
                    size="sm"
                    radius="xl"
                    component="a"
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener"
                    rightSection={<IconArrowRight size={14} />}
                    style={{
                      background: "white",
                      color: "var(--mantine-color-dark-9)",
                      border: "1px solid var(--mantine-color-gray-2)",
                      boxShadow: "0 2px 6px -2px rgba(0,0,0,0.08)",
                      fontWeight: 600,
                    }}
                  >
                    Fale com a gente
                  </Button>
                </Card>
              </Stack>
            </Box>
          </Grid.Col>
        </Grid>
      </Card>

      <Card
        mt="xl"
        maw={960}
        mx="auto"
        p={{ base: 28, md: 40 }}
        radius="xl"
        withBorder
        bg="gray.0"
        style={{ borderColor: "var(--mantine-color-gray-2)" }}
      >
        <Group justify="space-between" align="center" gap="md" wrap="wrap" mb="lg">
          <Group gap="sm" wrap="nowrap" align="center">
            <ThemeIcon
              size={44}
              radius="md"
              variant="light"
              color="green"
              style={{ flexShrink: 0 }}
            >
              <IconShield size={24} stroke={2.2} />
            </ThemeIcon>
            <Box>
              <Text fw={700} fz={{ base: 18, md: 22 }} c="dark.9" lh={1.2}>
                Manutenção{" "}
                <Text component="span" inherit c="gray.6" fw={600}>
                  (opcional)
                </Text>
              </Text>
              <Text c="gray.7" fz={{ base: 13, md: 14 }} mt={2}>
                Pra quem quer suporte contínuo, correção de bugs e visitas técnicas.
              </Text>
            </Box>
          </Group>
          <Group align="baseline" gap={4}>
            <Text
              fz={{ base: 28, md: 34 }}
              fw={800}
              c="dark.9"
              style={{ letterSpacing: "-0.03em" }}
            >
              R$19
            </Text>
            <Text fz={{ base: 15, md: 16 }} c="gray.6" fw={500}>
              /mês
            </Text>
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" verticalSpacing="sm">
          {maintenanceFeatures.map((f) => (
            <Group key={f} gap={10} wrap="nowrap" align="center">
              <ThemeIcon
                size={22}
                radius="xl"
                variant="light"
                color="green"
                style={{ flexShrink: 0 }}
              >
                <IconCheck size={13} stroke={3} />
              </ThemeIcon>
              <Text fz={{ base: 14, md: 15 }} c="dark.7" lh={1.4} fw={500}>
                {f}
              </Text>
            </Group>
          ))}
        </SimpleGrid>

        <Text c="gray.6" fz={{ base: 13, md: 14 }} lh={1.55} mt="lg">
          Sem o plano, o sistema continua funcionando normalmente pra sempre. Mesmo sem pagar essa
          mensalidade, você pode optar por um atendimento presencial avulso: R$50 por visita.
        </Text>
      </Card>

      <Stack mt={96} gap="lg" id="add-ons" maw={960} mx="auto" style={{ scrollMarginTop: 80 }}>
        <Stack gap={6} maw={640}>
          <Group gap="sm" wrap="nowrap" align="center">
            <ThemeIcon
              size={44}
              radius="md"
              variant="gradient"
              gradient={{ from: "green.7", to: "green.4", deg: 135 }}
              style={{ flexShrink: 0 }}
            >
              <IconTrendingUp size={24} stroke={2.2} />
            </ThemeIcon>
            <Title
              order={3}
              fz={{ base: 22, md: 28 }}
              fw={700}
              lh={1.15}
              style={{ letterSpacing: "-0.02em" }}
            >
              Para quando o negócio crescer.
            </Title>
          </Group>
          <Text c="gray.7" fz={{ base: 14, md: 15 }} lh={1.55}>
            Adicionais cobrados à parte, ativados só se e quando você quiser. Nada vem empurrado no
            pacote inicial.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {addOns.map((a, idx) => (
            <Reveal key={a.title} delay={idx * 60} style={{ height: "100%" }}>
              <Card
                padding="lg"
                radius="lg"
                withBorder
                className="feature-card"
                style={{ height: "100%", borderColor: "var(--mantine-color-gray-2)" }}
              >
                <Group align="flex-start" gap="md" wrap="nowrap">
                  <ThemeIcon size={40} radius="md" variant="light" color="green">
                    <a.icon size={20} />
                  </ThemeIcon>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group justify="space-between" align="flex-start" gap="xs" wrap="nowrap">
                      <Text fw={700} fz={{ base: 15, md: 16 }} c="dark.9" lh={1.25}>
                        {a.title}
                      </Text>
                      <Badge
                        variant="light"
                        color="green"
                        size="sm"
                        radius="sm"
                        style={{ flexShrink: 0, fontWeight: 700 }}
                      >
                        {a.price}
                      </Badge>
                    </Group>
                    <Text c="gray.7" fz={{ base: 13, md: 14 }} mt={6} lh={1.55}>
                      {a.description}
                    </Text>
                  </Box>
                </Group>
              </Card>
            </Reveal>
          ))}
        </SimpleGrid>
      </Stack>
    </Section>
  );
}
